import moment from "moment";
import firebase from "firebase/compat/app";
import { CalendarInterval } from "../enums/dateIntervalCalendar.enum";
import { Timestamp } from "firebase/firestore";
import { isNoEmptyArray } from "@pineapplelab/util";
import { Table } from "../models/table";
import { TemplateRef } from "@angular/core";
import { MediaLibraryFile } from "../enums/mediaLibraryFileType.enum";
import { ChapterContentType } from "../enums/chapterContentType.enum";
import { Observable } from "rxjs";

export function formatTime(seconds: number) {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);
	return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}

export function convertTimestampToDate(timestamp: number): Date {
	return new Date(timestamp * 1000);
}

export function takeOffUnderscorePropertiesInModel(json: any): any {
	return Object.keys(json)
		.filter(key => !key.startsWith('_'))
		.reduce((obj: { [key: string]: any }, key) => {
			obj[key] = json[key];
			return obj;
		}, {});
}

export function convertLocalDateToUTC(localDate: Date): Date {
	const utcYear = localDate.getUTCFullYear();
	const utcMonth = localDate.getUTCMonth();
	const utcDay = localDate.getUTCDate();
	const utcHours = localDate.getUTCHours();
	const utcMinutes = localDate.getUTCMinutes();
	const utcSeconds = localDate.getUTCSeconds();

	return new Date(Date.UTC(utcYear, utcMonth, utcDay, utcHours, utcMinutes, utcSeconds));
}

export const MY_FORMATS = {
	parse: {
		dateInput: ' MM/DD/YYYY',
	},
	display: {
		dateInput: 'MM/DD/yyyy',
		monthYearLabel: 'YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'YYYY',
	},
};

export function hasDatePassed(date: Date | firebase.firestore.Timestamp): boolean {
	if (date instanceof Date) {
		return moment(date).isBefore(new Date());
	}

	return moment(new Date(date?.seconds * 1000)).isBefore(new Date());
}

export function getStartOfWeek(today: Date): Date {
	const day = today.getDay();
	const diff = today.getDate() - day + (day === 0 ? -6 : 1);
	return new Date(today.setDate(diff));
}

export function getEndOfWeek(today: Date): Date {
	const startOfWeek = getStartOfWeek(today);
	return new Date(startOfWeek.setDate(startOfWeek.getDate() + 6));
}

export function isWeek(start: Date, end: Date, diffInDays: number): boolean {
	return diffInDays === 6 && start.getDay() === 1 && end.getDay() === 0;
}

export function isMonth(start: Date, end: Date): boolean {
	return (
		start.getDate() === 1 &&
		end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate() &&
		start.getMonth() === end.getMonth() &&
		start.getFullYear() === end.getFullYear()
	);
}

export function isQuarter(start: Date, end: Date): boolean {
	const startQuarter = Math.floor(start.getMonth() / 3);
	const endQuarter = Math.floor(end.getMonth() / 3);

	return (
		startQuarter === endQuarter &&
		start.getDate() === 1 &&
		end.getDate() === new Date(end.getFullYear(), (startQuarter + 1) * 3, 0).getDate() &&
		start.getFullYear() === end.getFullYear()
	);
}

export function isYear(start: Date, end: Date): boolean {
	return (
		start.getDate() === 1 &&
		start.getMonth() === 0 &&
		end.getDate() === 31 &&
		end.getMonth() === 11 &&
		start.getFullYear() === end.getFullYear()
	);
}

export function getDateRange(start: Date, end: Date): CalendarInterval {
	const diffInDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

	if (isWeek(start, end, diffInDays)) {
		return CalendarInterval.WEEK;
	} else if (isMonth(start, end)) {
		return CalendarInterval.MONTH;
	} else if (isQuarter(start, end)) {
		return CalendarInterval.TRIMESTER;
	} else if (isYear(start, end)) {
		return CalendarInterval.YEAR;
	} else {
		return null;
	}
}

export function base64toBlob(
	base64Data: string,
	contentType: string = 'image/png'
): Blob {
	const byteCharacters = atob(base64Data.split(',')[1]);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += 512) {
		const slice = byteCharacters.slice(offset, offset + 512);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	return new Blob(byteArrays, { type: contentType });
}

export function blobToFile(blob: Blob, fileName: string): File {
	const b: any = blob;
	b.lastModifiedDate = new Date();
	b.name = fileName;

	const file = new File([blob], fileName, { type: blob.type });
	return file;
}

export function filterQueryByDate(snapshot: any, filters: { from: Date; to: Date }): any {
	if (filters?.from) {
		const dateFrom = Timestamp.fromDate(filters.from);
		snapshot.where('created_at', '>=', dateFrom);
	}

	if (filters?.to) {
		const dateTo = Timestamp.fromDate(filters.to);
		snapshot.where('created_at', '<=', dateTo);
	}

	return snapshot;
}

export function  getColumns(columns : any[], tableFieldToTemplateRelation : { [key: string]: TemplateRef<any> }  ): Table.IColumn[] {
	if (!isNoEmptyArray(columns)) {
		return [];
	}

	return columns.map(({ field, label, templateName }) => {
		const template = tableFieldToTemplateRelation[templateName];
		if (template) {
			return { field, label, template, showOnlyTemplate: true };
		} else {
			return { field, label };
		}
	});
}

export function scrollTo(id: string): void {
	const element = document.getElementById(id);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
}

export const MEDIA_FILE_TYPE_TO_STRING: { [type in MediaLibraryFile]: string } = {
	0: 'general.video',
	1: 'general.pdf',
	2: 'general.image'
}

export const STUDENT_NAME_REGEX = new RegExp(/^[A-Za-zÁÉÍÓÚáéíóúñÑ' -]+$/);

export const FORM_LINK_REGEX = new RegExp(/^(https?:\/\/)?[\w\-]+(\.[\w\-]+)+([\/\w\-.\?&=#]*)$/);

export const CLIENT_USERNAME_REGEXP = new RegExp(/^[A-Za-z0-9\-]+$/);

export const CLIENT_COMPANY_NAME_REGEXP = new RegExp(/^[A-Za-z0-9 ´@#\$%\^&\*\(\)\-\_\+\=\[\]\{\};:'",.<>\/?¡!¿]+$/);

export const MEDIA_LIBRARY_FILE_TO_CHAPTER_CONTENT_TYPE: { [type in MediaLibraryFile]: ChapterContentType } = {
	[MediaLibraryFile.VIDEO]: ChapterContentType.VIDEO,
	[MediaLibraryFile.PDF]: ChapterContentType.PDF,
	[MediaLibraryFile.IMAGE]: ChapterContentType.IMAGE
}

export const USERNAME_REGEX = new RegExp(/^(?!.*@)(?!.*\.\w{2,}$)[a-zA-Z0-9._]+$/)